import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { PackageDimensions } from '../types';

const COLLECTION_NAME = 'packageDimensions';

/**
 * Get all package dimensions
 */
export const getPackageDimensions = async (): Promise<PackageDimensions[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('isDefault', 'desc'),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as PackageDimensions[];
  } catch (error) {
    console.error('Error fetching package dimensions:', error);
    throw new Error('Failed to fetch package dimensions');
  }
};

/**
 * Get default package dimensions
 */
export const getDefaultPackageDimensions = async (): Promise<PackageDimensions | null> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('isDefault', '==', true)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    } as PackageDimensions;
  } catch (error) {
    console.error('Error fetching default package dimensions:', error);
    throw new Error('Failed to fetch default package dimensions');
  }
};

/**
 * Create new package dimensions
 */
export const createPackageDimensions = async (dimensionsData: Omit<PackageDimensions, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    // If this is being set as default, unset other defaults first
    if (dimensionsData.isDefault) {
      await unsetAllDefaults();
    }
    
    // Clean the data to remove undefined values for Firestore
    const cleanedData: any = {
      name: dimensionsData.name,
      length: dimensionsData.length,
      width: dimensionsData.width,
      height: dimensionsData.height,
      description: dimensionsData.description || '',
      isDefault: dimensionsData.isDefault || false,
    };
    
    // Only include weight if it's a valid number
    if (dimensionsData.weight !== undefined && dimensionsData.weight !== null && !isNaN(dimensionsData.weight)) {
      cleanedData.weight = dimensionsData.weight;
    }
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...cleanedData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating package dimensions:', error);
    throw new Error('Failed to create package dimensions');
  }
};

/**
 * Update existing package dimensions
 */
export const updatePackageDimensions = async (id: string, updates: Partial<PackageDimensions>): Promise<void> => {
  try {
    // If this is being set as default, unset other defaults first
    if (updates.isDefault) {
      await unsetAllDefaults();
    }
    
    // Clean the updates to remove undefined values for Firestore
    const cleanedUpdates: any = {};
    
    // Only include fields that have valid values
    Object.keys(updates).forEach(key => {
      const value = updates[key as keyof PackageDimensions];
      
      if (key === 'weight') {
        // Only include weight if it's a valid number
        if (value !== undefined && value !== null && !isNaN(value as number)) {
          cleanedUpdates.weight = value;
        }
      } else if (value !== undefined) {
        cleanedUpdates[key] = value;
      }
    });
    
    const docRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(docRef, {
      ...cleanedUpdates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating package dimensions:', error);
    throw new Error('Failed to update package dimensions');
  }
};

/**
 * Delete package dimensions
 */
export const deletePackageDimensions = async (id: string): Promise<void> => {
  try {
    // Check if this is the default dimensions
    const dimensionsDoc = await getDoc(doc(db, COLLECTION_NAME, id));
    if (!dimensionsDoc.exists()) {
      throw new Error('Package dimensions not found');
    }
    
    const dimensionsData = dimensionsDoc.data() as PackageDimensions;
    
    // If this was the default, set another as default if available
    if (dimensionsData.isDefault) {
      const allDimensions = await getPackageDimensions();
      const otherDimensions = allDimensions.filter(dim => dim.id !== id);
      
      if (otherDimensions.length > 0) {
        await updatePackageDimensions(otherDimensions[0].id!, { isDefault: true });
      }
    }
    
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  } catch (error) {
    console.error('Error deleting package dimensions:', error);
    throw new Error('Failed to delete package dimensions');
  }
};

/**
 * Set package dimensions as default
 */
export const setDefaultPackageDimensions = async (id: string): Promise<void> => {
  try {
    // Unset all defaults first
    await unsetAllDefaults();
    
    // Set the selected dimensions as default
    await updatePackageDimensions(id, { isDefault: true });
  } catch (error) {
    console.error('Error setting default package dimensions:', error);
    throw new Error('Failed to set default package dimensions');
  }
};

/**
 * Helper function to unset all default flags
 */
const unsetAllDefaults = async (): Promise<void> => {
  const dimensions = await getPackageDimensions();
  const defaultDimensions = dimensions.filter(dim => dim.isDefault);
  
  const updatePromises = defaultDimensions.map(dim => 
    updateDoc(doc(db, COLLECTION_NAME, dim.id!), {
      isDefault: false,
      updatedAt: serverTimestamp(),
    })
  );
  
  await Promise.all(updatePromises);
};

/**
 * Calculate volume in cubic inches
 */
export const calculateVolume = (dimensions: PackageDimensions): number => {
  return dimensions.length * dimensions.width * dimensions.height;
};

/**
 * Calculate dimensional weight (DIM weight) for common carriers
 * Most carriers use 139 as the divisor for domestic shipments
 */
export const calculateDimensionalWeight = (dimensions: PackageDimensions, divisor: number = 139): number => {
  const volume = calculateVolume(dimensions);
  return Math.ceil(volume / divisor);
};

/**
 * Validate package dimensions data
 */
export const validatePackageDimensions = (dimensions: Partial<PackageDimensions>): string[] => {
  const errors: string[] = [];
  
  if (!dimensions.name?.trim()) {
    errors.push('Package name is required');
  }
  
  if (!dimensions.length || dimensions.length <= 0) {
    errors.push('Length must be greater than 0');
  }
  
  if (!dimensions.width || dimensions.width <= 0) {
    errors.push('Width must be greater than 0');
  }
  
  if (!dimensions.height || dimensions.height <= 0) {
    errors.push('Height must be greater than 0');
  }
  
  if (!dimensions.weight || dimensions.weight <= 0) {
    errors.push('Package weight is required and must be greater than 0');
  }
  
  // Reasonable size limits (adjust based on your shipping needs)
  const maxDimension = 108; // 9 feet in inches (common carrier limit)
  
  if (dimensions.length && dimensions.length > maxDimension) {
    errors.push(`Length cannot exceed ${maxDimension} inches`);
  }
  
  if (dimensions.width && dimensions.width > maxDimension) {
    errors.push(`Width cannot exceed ${maxDimension} inches`);
  }
  
  if (dimensions.height && dimensions.height > maxDimension) {
    errors.push(`Height cannot exceed ${maxDimension} inches`);
  }
  
  // Check for reasonable volume (165 cubic feet is common large package limit)
  if (dimensions.length && dimensions.width && dimensions.height) {
    const volume = dimensions.length * dimensions.width * dimensions.height;
    const maxVolume = 165 * 1728; // 165 cubic feet in cubic inches
    
    if (volume > maxVolume) {
      errors.push('Package dimensions are too large for standard shipping');
    }
  }
  
  return errors;
};

/**
 * Get suggested package dimensions based on order weight/contents
 */
export const getSuggestedDimensions = async (orderWeight?: number): Promise<PackageDimensions[]> => {
  try {
    const allDimensions = await getPackageDimensions();
    
    if (!orderWeight) {
      return allDimensions;
    }
    
    // Sort by suitability based on weight
    return allDimensions.sort((a, b) => {
      const aVolume = calculateVolume(a);
      const bVolume = calculateVolume(b);
      
      // Prefer packages with weight capacity close to but greater than order weight
      const aWeightScore = a.weight ? Math.abs(a.weight - orderWeight) : 1000;
      const bWeightScore = b.weight ? Math.abs(b.weight - orderWeight) : 1000;
      
      // Combine weight and volume scores
      return aWeightScore - bWeightScore || aVolume - bVolume;
    });
  } catch (error) {
    console.error('Error getting suggested dimensions:', error);
    return [];
  }
};